/* implDeferred.js -- W-081 (2026-10-05): THE ONE OWNER of three questions about a DEFERRED implementation line's existing book.
 * Read by the engine (computeImplementationRevenue: the opening), the pre-flight (P8's Zero-mode block, P19's reconciliation)
 * and the Deferred Revenue page's Implementation panel (the same three answers, shown beside the controls). One rule each, so
 * the screen, the check and the model cannot disagree (Principle 0).
 *
 * 1. implOpeningBalance -- the deferred balance the forecast starts from (Existing mode).
 *      LINKED to a real account (utils/implKeys.js implDrLinkedKey)  -> THAT ACCOUNT'S CLOSE-MONTH ACTUAL (bs_actuals[leaf][close]),
 *                                                                      derived; the manual field is not read (Brian, 10-05)
 *      SYNTHETIC or unanswered                                        -> the manual opening_bs_balance (it has no account)
 *    `bsActuals` undefined (a caller that never passes it) -> the manual field, exactly as before W-081.
 * 2. implZeroModeTrap -- a line LINKED to an account that holds a balance at the close, in ZERO start mode: the engine starts the
 *    balance from 0 and the account's run-off is silently dropped. P8 blocks it at go-live; the panel says it.
 * 3. implGridReconciliation -- the "Monthly GAAP Revenue (actuals)" grid as a CHECK (its own help text: "Must match IS actuals"):
 *      sum of the billings ("Total Implementation Revenue (actuals)") through the close
 *    - sum of the GAAP revenue recognised (the grid) through the close
 *    = the deferred balance left at the close, which must equal the opening the forecast starts from.
 *    A WARNING when they differ by a cent or more (P19 and the panel). An EMPTY grid is NOT CHECKED (null, reason 'grid_empty'):
 *    nothing was entered to reconcile. The grid never drives a number.
 * 4. implLinkAllowed -- W-081 follow-up (Brian, 2026-10-05): EXISTING mode means a deferred balance at the close, and that balance
 *    lives in a REAL account -- so a DEFERRED line in Existing mode may not answer "Synthetic (forecast-only)". Synthetic stays
 *    valid in Zero mode (a new stream, no history); an IMMEDIATE line has no deferred balance and is never judged; an UNANSWERED
 *    line (NULL) is allowed here -- P6 blocks it at go-live. Read by the panel (which offers Synthetic only where this allows it),
 *    the pre-flight (P6) and the server (its mirror, verify-wall-mirrors pair M12, judged on the RESULTING row of a PUT).
 * Plain ASCII on purpose (section 12.5 rule 55). */
import { implDrLinkedKey, implDrSyntheticKey } from './implKeys';

const r2 = n => Math.round((Number(n) || 0) * 100) / 100;
const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

/** The close-month balance of the real account a line is linked to, or null when it is not linked. */
export function implLinkedCloseBalance(line, cutoff, bsActuals) {
  const key = line ? implDrLinkedKey(line.id, line.bs_line_key) : null;
  if (!key || !cutoff || !bsActuals) return null;
  return r2(((bsActuals || {})[key] || {})[cutoff]);
}

/** The opening deferred balance the engine starts from (see 1. above). */
export function implOpeningBalance(line, cutoff, bsActuals) {
  if (bsActuals !== undefined) {
    const linked = implLinkedCloseBalance(line, cutoff, bsActuals);
    if (linked !== null) return linked;
  }
  return num(line && line.opening_bs_balance);
}

/** The Zero-mode trap (see 2. above): null when absent, else { line, account, balance, close }. */
export function implZeroModeTrap(line, cutoff, bsActuals) {
  if (!line || line.enabled === false || line.recognition_mode !== 'deferred') return null;
  if ((line.start_mode || 'zero') === 'existing') return null;
  const bal = implLinkedCloseBalance(line, cutoff, bsActuals);
  if (bal === null || Math.abs(bal) < 0.01) return null;
  return { line_id: line.id, name: line.name || ('implementation line ' + line.id), account: line.bs_line_key, balance: bal, close: cutoff };
}

/** The one answer to "may this line's balance-sheet answer stand?" (see 4. above): false ONLY for a DEFERRED line in EXISTING
 *  start mode whose answer is its own synthetic ("Synthetic (forecast-only)"). `line` is line-shaped: { id, recognition_mode,
 *  start_mode, bs_line_key } (a missing start mode reads as zero -- the engine's default). */
export function implLinkAllowed(line) {
  if (!line || line.recognition_mode !== 'deferred') return true;
  if ((line.start_mode || 'zero') !== 'existing') return true;
  return line.bs_line_key !== implDrSyntheticKey(line.id);
}
/** The sentence the panel, the pre-flight and the server all say when implLinkAllowed refuses. */
export const IMPL_EXISTING_NEEDS_ACCOUNT = 'Existing mode carries a deferred balance from the close \u2014 link the account that holds it';

/** W-081 part 2: the deferred-revenue accounts RESERVED at Step 7 for implementation revenue (companies.dr_line_reservations,
 *  { key: 'implementation' } or null) that no enabled DEFERRED implementation line links yet -- each blocks go-live (P20; the server
 *  backstop asks the same question in SQL). A reservation linked by such a line is satisfied (the mark may stay; it is then inert). */
export function implReservationGaps(reservations, implementationLines) {
  const linked = new Set((implementationLines || [])
    .filter(l => l && l.enabled !== false && l.recognition_mode === 'deferred')
    .map(l => implDrLinkedKey(l.id, l.bs_line_key)).filter(Boolean));
  return Object.keys(reservations || {}).filter(k => reservations[k] === 'implementation' && !linked.has(k));
}

/** The reconciliation (see 3. above): null when it does not apply; else the two sums, the implied and opening balances, the
 *  difference, the months each grid carries, and `ok`. */
export function implGridReconciliation(line, cutoff, bsActuals) {
  if (!line || line.enabled === false || line.recognition_mode !== 'deferred' || !cutoff) return null;
  if ((line.start_mode || 'zero') !== 'existing') return null;
  const billedBy = line.existing_total_impl || {}, gaapBy = line.existing_monthly_rev || {};
  const gaapMonths = Object.keys(gaapBy).filter(m => m <= cutoff && Math.abs(num(gaapBy[m])) >= 0.005).sort();
  if (!gaapMonths.length) return { applies: false, reason: 'grid_empty' };
  const billedMonths = Object.keys(billedBy).filter(m => m <= cutoff && Math.abs(num(billedBy[m])) >= 0.005).sort();
  const billed = r2(billedMonths.reduce((s, m) => s + num(billedBy[m]), 0));
  const recognised = r2(gaapMonths.reduce((s, m) => s + num(gaapBy[m]), 0));
  const implied = r2(billed - recognised);
  const linked = implLinkedCloseBalance(line, cutoff, bsActuals);
  const opening = linked !== null ? linked : num(line.opening_bs_balance);
  const diff = r2(implied - opening);
  return {
    applies: true, ok: Math.abs(diff) < 0.01,
    billed, recognised, implied, opening, openingSource: linked !== null ? 'account' : 'entered', diff,
    billedMonths, gaapMonths,
  };
}
